export interface FAQVisual {
  /** Emoji or short pictogram shown as the focal point of the visual */
  emoji: string;
  /** Tailwind gradient classes (from-... via-... to-...) using semantic / brand tokens */
  gradient: string;
  /** Short caption rendered inside the visual */
  caption: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  /** Step-by-step in-app navigation guide, e.g. ["Home", "My Rides", "Host Profile"] */
  navigation?: string[];
  /** Optional visual illustration shown above the answer */
  visual?: FAQVisual;
}

export interface FAQCategory {
  id: string;
  title: string;
  description: string;
  icon: string; // lucide icon name
  faqs: FAQ[];
}

/**
 * AVAILABILITY NOTE
 * Duri Ridesharing technically works anywhere in the world - you can sign up and try it.
 * However, ride matching, payments and verification are officially activated only in:
 *   - India 🇮🇳 - Active (full launch, focus on Telugu states: Andhra Pradesh & Telangana)
 *   - USA 🇺🇸 - Beta (limited launch, expanding city by city)
 * Other countries: try-only / waitlist mode until official activation.
 */

export const faqCategories: FAQCategory[] = [
  {
    id: "why-duri",
    title: "Why Duri (vs Uber/Ola/Others)",
    description: "How Duri is different from Uber, Ola, taxis, and other ridesharing apps - and why it matters.",
    icon: "Sparkles",
    faqs: [
      {
        id: "vs-uber-ola",
        question: "How is Duri different from Uber, Ola or other taxi/ride-hailing apps?",
        answer:
          "Uber and Ola are commercial taxi services - a paid driver picks you up in a commercial vehicle and you pay a market fare with surge pricing. Duri is true ridesharing: a verified private car owner (Host) is already going on a trip and shares empty seats with Guests heading the same way. There is no commercial driver, no surge pricing, and the fare only covers a fair share of fuel and tolls. Result: you pay a fraction of a taxi fare, the Host saves on fuel, and one less car runs empty on the highway.",
        navigation: ["Home"],

        visual: { emoji: "🚕", gradient: "from-primary/15 via-primary/5 to-accent/10", caption: "Why Duri" },
      },
      {
        id: "vs-other-rideshare",
        question: "How is Duri different from other ridesharing services in India?",
        answer:
          "Most carpooling apps in India stop at matching a driver and rider. Duri goes further: Aadhaar + DL + RC verification via Vaahan/mParivahan, live family tracking, one-tap SOS, QR/PIN boarding, AI fare engine, seat-level Male/Female preferences, ride group chat, enroute pickup/drop, daily-commute matching, AQI health advisory, and full Telugu language support. We are built community-first for India, not retrofitted from a global product.",
        navigation: ["Home", "Why Duri"],

        visual: { emoji: "🇮🇳", gradient: "from-primary/15 via-primary/5 to-accent/10", caption: "Why Duri" },
      },
      {
        id: "main-motto",
        question: "What is Duri's main motto and mission?",
        answer:
          "Rideshare to reduce carbon emissions, cut traffic congestion, and make travel affordable for everyone. Every shared seat means one less car on the road, less fuel burned, and less CO2 in the air. Beyond saving money, Duri helps you make new friends through Companion Match, explore new places, and discover local culture, food, and historic attractions on the way - turning every trip into a small adventure.",
        navigation: ["Home", "About Duri", "Our Mission"],

        visual: { emoji: "🌱", gradient: "from-primary/15 via-primary/5 to-accent/10", caption: "Why Duri" },
      },
      {
        id: "who-benefits",
        question: "Who benefits from using Duri?",
        answer:
          "Everyone wins. 🚗 Hosts recover fuel and toll costs and earn rewards on otherwise empty trips - including drop-off / return empty rides that would normally run with zero passengers. 🧑 Guests travel intercity at a fraction of bus or taxi fare, in a comfortable private car, with verified people. 🌍 The community benefits from fewer cars on the road, less traffic, and lower carbon emissions. 👫 Solo travelers get Companion Match to find safe travel buddies and make new friends.",
        navigation: ["Home", "How Duri Works "],

        visual: { emoji: "🤝", gradient: "from-primary/15 via-primary/5 to-accent/10", caption: "Why Duri" },
      },
      {
        id: "empty-return-rides",
        question: "I'm driving back empty after a drop-off. Can I still benefit?",
        answer:
          "Absolutely - this is one of Duri's biggest wins. Publish your return/empty leg as a ride and Guests waiting on that exact route get instantly notified. You recover fuel and toll cost on a trip you were doing anyway, and someone gets a comfortable ride home. Use Return Rides Made Easy to publish both legs in one flow.",
        navigation: ["Host a Ride", "Return Trip"],

        visual: { emoji: "↩️", gradient: "from-primary/15 via-primary/5 to-accent/10", caption: "Why Duri" },
      },
      {
        id: "explore-attractions",
        question: "Can Duri help me explore new places, food and culture along the way?",
        answer:
          "Yes! Every popular Duri route is enriched with Enroute Attractions - historic landmarks, famous local food stops, temples, viewpoints and cultural spots between your origin and destination. Hosts often suggest short stops, and the Ride Group Chat is a great place to plan a quick coffee or sightseeing break together.",
        navigation: ["Search Rides", "Select Route", "Enroute Attractions"],

        visual: { emoji: "🏞️", gradient: "from-primary/15 via-primary/5 to-accent/10", caption: "Why Duri" },
      },
      {
        id: "companion-friends",
        question: "Can I make new friends while traveling?",
        answer:
          "Yes - that's the heart of ridesharing. Companion Match helps you find co-riders with similar interests, language, or travel patterns. Many regular Duri commuters have built lasting friendships with people they now share their daily office commute or weekend getaways with.",
        navigation: ["Search Rides", "Companion Match"],

        visual: { emoji: "👫", gradient: "from-primary/15 via-primary/5 to-accent/10", caption: "Why Duri" },
      },
    ],
  },
  {
    id: "getting-started",
    title: "Getting Started",
    description: "New to Duri? Learn how to sign up, verify your account, and take your first ride.",
    icon: "Rocket",
    faqs: [
      {
        id: "what-is-duri",
        question: "What is Duri and how does it work?",
        answer:
          "Duri is a secure intercity and daily-commute ridesharing app. Hosts (car owners) post rides with available seats, and Guests (riders) book a seat to share travel costs. Duri Ridesharing technically works anywhere in the world and you're welcome to try it - but it is officially activated in India (Active) and USA (Beta) today. Every user is verified, every trip is tracked live, and SOS support is one tap away.",
        navigation: ["Home", "Host/Book a Ride"],

        visual: { emoji: "🚀", gradient: "from-primary/20 via-primary/10 to-secondary", caption: "Get Started" },
      },
      {
        id: "where-available",
        question: "In which countries is Duri available?",
        answer:
          "Duri works globally as an app - you can download and explore it from anywhere. Officially: 🇮🇳 India is Active (full launch with focus on Andhra Pradesh & Telangana, expanding to Bengaluru, Chennai, Pune). 🇺🇸 USA is in Beta (limited city rollout, growing weekly). In all other countries, you can join the waitlist and we'll notify you the moment Duri activates in your region.",
        navigation: ["Home", "Settings", "Country & Region"],

        visual: { emoji: "🚀", gradient: "from-primary/20 via-primary/10 to-secondary", caption: "Get Started" },
      },
      {
        id: "how-to-signup",
        question: "How do I sign up on the Duri app?",
        answer:
          "Download Duri from the Play Store or App Store, enter your phone number, verify the OTP, and complete identity verification (Aadhaar in India / SSN-last-4 + Driver's Licence in USA Beta). Hosts also add their Driving Licence (DL) and vehicle Registration Certificate (RC), validated against Vaahan/mParivahan in India and DMV partners in the USA.",
        navigation: ["Open App", "Sign Up", "Verify Phone OTP", "Complete ID Verification"],

        visual: { emoji: "🚀", gradient: "from-primary/20 via-primary/10 to-secondary", caption: "Get Started" },
      },
      {
        id: "languages",
        question: "Which languages does Duri support?",
        answer:
          "Duri currently supports English and Telugu, with full app translation, support, and notifications in Telugu for users across Andhra Pradesh and Telangana. English is the default in the USA Beta. More Indian and global languages are being added.",
        navigation: ["Home", "Profile", "Settings", "Language"],

        visual: { emoji: "🚀", gradient: "from-primary/20 via-primary/10 to-secondary", caption: "Get Started" },
      },
      {
        id: "host-vs-guest",
        question: "What's the difference between a Host and a Guest?",
        answer:
          "A Host is a verified car owner who publishes a ride and offers seats. A Guest is a verified rider who books a seat to share the trip cost. The same person can switch between Host and Guest roles anytime.",
        navigation: ["Home", "Profile", "Switch Role (Host ⇄ Guest)"],

        visual: { emoji: "🚀", gradient: "from-primary/20 via-primary/10 to-secondary", caption: "Get Started" },
      },
    ],
  },
  {
    id: "availability",
    title: "Availability & Coverage",
    description: "Where Duri operates today and upcoming country & city expansions.",
    icon: "MapPin",
    faqs: [
      {
        id: "global-availability",
        question: "Does Duri work outside India and USA?",
        answer:
          "The Duri app technically works anywhere in the world - anyone can download, sign up and explore. However, ride matching and payments are officially activated only in 🇮🇳 India (Active) and 🇺🇸 USA (Beta). In all other countries you can join the waitlist and Duri will notify you the moment we activate locally.",
        navigation: ["Home", "Settings", "Country & Region"],

        visual: { emoji: "🌍", gradient: "from-accent/20 via-secondary to-primary/10", caption: "Worldwide" },
      },
      {
        id: "cities",
        question: "Which cities does Duri currently serve?",
        answer:
          "🇮🇳 India (Active): Hyderabad, Vijayawada, Visakhapatnam, Tirupati, Guntur, Warangal, Kurnool, Rajahmundry and most intercity routes between them. Expansion to Bengaluru, Chennai, and Pune underway. 🇺🇸 USA (Beta): select cities in California, Texas and New Jersey - expanding weekly.",
        navigation: ["Home", "Settings"],

        visual: { emoji: "🌍", gradient: "from-accent/20 via-secondary to-primary/10", caption: "Worldwide" },
      },
      {
        id: "international",
        question: "Will Duri be available in more countries?",
        answer:
          "Yes - the app is internationalized with multi-language, multi-currency, and multi-timezone support. After scaling USA Beta, next planned activations include UK, UAE, Canada and Australia.",
        navigation: ["Home", "Settings", "Country & Region"],

        visual: { emoji: "🌍", gradient: "from-accent/20 via-secondary to-primary/10", caption: "Worldwide" },
      },
      {
        id: "ride-frequency",
        question: "How many rides are available daily?",
        answer:
          "Thousands of intercity and daily-commute rides are listed every day across our active routes in India, with growing volume across USA Beta cities. Check the Available Rides page for live inventory.",
        navigation: ["Home", "Available Rides", "Today"],

        visual: { emoji: "🌍", gradient: "from-accent/20 via-secondary to-primary/10", caption: "Worldwide" },
      },
      {
        id: "rides-on-my-route",
        question: "How do I know if rides are available on my route or not?",
        answer:
          "Open Available Rides from the home page or header, enter your From and To cities (and travel date if prompted), and browse live Host listings for that corridor. You'll see how many seats are open, departure times, and fares before you book. If nothing matches today, save a Future Ride Need so Duri notifies you the moment a Host publishes on your route. You can also check Popular Routes on the site footer for corridors where Duri already has strong supply.",
        navigation: ["Home", "Available Rides", "Search Your Route"],

        visual: { emoji: "🛣️", gradient: "from-accent/20 via-secondary to-primary/10", caption: "Worldwide" },
      },
    ],
  },
  {
    id: "trust-safety",
    title: "Trust & Safety",
    description: "Verification, live tracking, SOS - how Duri keeps every ride safe.",
    icon: "ShieldCheck",
    faqs: [
      {
        id: "user-verification",
        question: "How are users verified on Duri?",
        answer:
          "Every user goes through Phone OTP + government ID verification (Aadhaar in India, SSN-last-4 + DL in USA Beta). Hosts additionally undergo Driving Licence and Vehicle RC validation through Vaahan/mParivahan (India) or DMV partners (USA). Manual back-office verification is available via our call center for edge cases.",
        navigation: ["Profile", "Verification Center", "Upload Documents"],

        visual: { emoji: "🪪", gradient: "from-emerald-500/15 via-primary/10 to-secondary", caption: "Verified & Safe" },
      },
      {
        id: "know-host-guest",
        question: "How do I get to know about my Host or Guest before the ride?",
        answer:
          "Before you confirm a booking, open the Host (or Guest) profile to see their verified name and photo, verification badges (Aadhaar / DL / RC for Hosts), trust score and star rating, total rides hosted/taken, recent ride feedback from real co-riders, languages spoken, Ride Bio with profession and hobbies, and shared interests for Companion Match. You can also send a quick message in the Pre-Ride Chat to ask anything you need - vehicle, baggage rules, pickup point - before you commit. This upfront transparency is core to Duri's mission of hassle-free, safe and joyful rides.",
        navigation: ["Search Rides", "Select Ride", "View Host Profile"],

        visual: { emoji: "👤", gradient: "from-emerald-500/15 via-primary/10 to-secondary", caption: "Verified & Safe" },
      },
      {
        id: "live-tracking",
        question: "Can my family track my ride in real time?",
        answer:
          "Yes. Live Tracking is enabled for every trip. Guests, Hosts, and nominated family members can follow the trip on a live map from pickup to drop, including ETA and route deviations.",
        navigation: ["My Rides", "Active Ride", "Share Live Location"],

        visual: { emoji: "📍", gradient: "from-emerald-500/15 via-primary/10 to-secondary", caption: "Verified & Safe" },
      },
      {
        id: "sos",
        question: "What happens when I press the SOS button?",
        answer:
          "One-tap SOS instantly alerts Duri's emergency response team and your nominated emergency contacts with your live location, ride details, and host/guest information. Our support line stays connected until you confirm you're safe.",
        navigation: ["Active Ride", "Safety", "SOS (long-press)"],

        visual: { emoji: "🆘", gradient: "from-emerald-500/15 via-primary/10 to-secondary", caption: "Verified & Safe" },
      },
      {
        id: "female-safety",
        question: "Are there special safety options for women travelers?",
        answer:
          "Yes. Seat Management lets hosts mark seats as Male/Female-only and add special seating instructions. Guests can filter rides accordingly and travel with a Companion using our Companion-driven Ride Search.",
        navigation: ["Search Rides", "Filters", "Female-only Seats"],

        visual: { emoji: "👩", gradient: "from-emerald-500/15 via-primary/10 to-secondary", caption: "Verified & Safe" },
      },
    ],
  },
  {
    id: "rides-booking",
    title: "Rides & Booking",
    description: "Searching, booking, boarding, and managing your rides.",
    icon: "Car",
    faqs: [
      {
        id: "ai-search",
        question: "How does AI-powered ride search work?",
        answer:
          "Duri's AI matches you with rides based on your route, timing flexibility, companion needs, seat preferences, and recurring commute patterns. It also helps Hosts auto-fill ride details when publishing.",
        navigation: ["Home", "Search Rides", "AI Smart Match"],

        visual: { emoji: "🤖", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "enroute-pickup",
        question: "Can I be picked up from a city on the way?",
        answer:
          "Yes - if the Host has enabled Enroute Pickup/Drop for that ride, you can request a pickup or drop in any city along the route. The fare is automatically adjusted based on distance.",
        navigation: ["Search Rides", "Select Ride", "Enroute Pickup", "Choose City"],

        visual: { emoji: "📌", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "qr-pin-boarding",
        question: "How do I board a ride?",
        answer:
          "At pickup, the Host scans your unique QR code or you share your 4-digit boarding PIN. This confirms your boarding, starts live tracking, and triggers ride-start notifications to your contacts.",
        navigation: ["My Rides", "Upcoming", "Show Boarding QR / PIN"],

        visual: { emoji: "🔢", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "return-rides",
        question: "Can I book return rides easily?",
        answer:
          "Yes. Use Return Rides Made Easy to host or book a round trip in a single flow - Duri remembers your destination and suggests matching return options.",
        navigation: ["Search Rides", "Round Trip", "Add Return Date"],

        visual: { emoji: "🔁", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "ride-alerts",
        question: "What if no ride is available on my route today?",
        answer:
          "Save it as a Future Ride Need. Duri sends you a push notification the moment a matching ride is published - and Hosts can see waiting demand to publish accordingly.",
        navigation: ["Search Rides", "Save for later"],

        visual: { emoji: "🔔", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "guests-waiting-on-route",
        question: "How do I know if any guests are waiting for a ride on my route?",
        answer:
          "As a Host, open Ride Requests from the header or home, then search your From → To corridor and travel date. You'll see Guests who have posted ride needs or are looking for a seat on that route, along with timing and seat preferences. Publishing your ride on a corridor with visible waiting demand helps you fill seats faster - especially on return or empty legs.",
        navigation: ["Home", "Ride Requests", "Search Your Route"],

        visual: { emoji: "🧳", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "route-place-demographics",
        question: "How do I know the demographics of the places on my route?",
        answer:
          "When you search or open a corridor (for example Hyderabad ↔ Vijayawada), Duri surfaces Enroute Attractions and city insights along the highway - landmarks, food stops, cultural spots, and local context for towns you pass through. Route pages and Popular Routes also highlight which corridors are most active. For health-related context, live AQI and air-quality advisories are shown for your full route during an active trip.",
        navigation: ["Search Rides", "Select Route", "Enroute Attractions"],

        visual: { emoji: "🗺️", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "host-approval",
        question: "Do Hosts auto-approve bookings?",
        answer:
          "Hosts choose their preference - Auto-approve for instant booking or Manual approve to review each request before confirming.",
        navigation: ["Host Profile", "Ride Settings", "Approval Mode"],

        visual: { emoji: "✅", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
      {
        id: "cancel-ride",
        question: "How do I cancel a ride?",
        answer:
          "Open the ride from your bookings and tap Cancel Ride. Cancellation policies and any applicable charges are shown upfront before confirmation.",
        navigation: ["My Rides", "Upcoming", "Select Ride", "Cancel Ride"],

        visual: { emoji: "❌", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Book a Ride" },
      },
    ],
  },
  {
    id: "vehicle-seats",
    title: "Vehicle & Seat Management",
    description: "Register your vehicle, configure seats, and manage offerings.",
    icon: "CarFront",
    faqs: [
      {
        id: "register-vehicle",
        question: "How do I register my vehicle as a Host?",
        answer:
          "Add your vehicle in the Host profile with make, model, RC, and seat layout. RC details are validated automatically via Vaahan/mParivahan in India and DMV partners in the USA Beta.",
        navigation: ["Profile", "Host Profile", "My Vehicles", "Add Vehicle"],

        visual: { emoji: "🚙", gradient: "from-secondary via-primary/10 to-accent/15", caption: "Seats & Vehicle" },
      },
      {
        id: "seat-layout",
        question: "Can I offer only specific seats in my car?",
        answer:
          "Yes. Configure your vehicle's Seat Layout, mark seats as reserved, and offer only the seats you want. Guests pick their preferred seat from a visual layout while booking.",
        navigation: ["Host Profile", "My Vehicles", "Seat Layout", "Configure Seats"],

        visual: { emoji: "🪑", gradient: "from-secondary via-primary/10 to-accent/15", caption: "Seats & Vehicle" },
      },
      {
        id: "male-female-seats",
        question: "Can I add Male/Female seating instructions?",
        answer:
          "Yes. Hosts can tag specific seats as Male-only or Female-only with special instructions, helping Guests choose comfortable seating - keeping every journey hassle-free, safe and joyful, in line with Duri's mission of memorable rides.",
        navigation: ["Host a Ride", "Settings", " Preferences"],

        visual: { emoji: "👥", gradient: "from-secondary via-primary/10 to-accent/15", caption: "Seats & Vehicle" },
      },
    ],
  },
  {
    id: "daily-commutes",
    title: "Daily Commute Carpool",
    description:
      "Office and weekday travel: set your commute once, get matched every week, and share fuel with verified co-riders.",
    icon: "Briefcase",
    faqs: [
      {
        id: "what-is-daily-commute",
        question: "What is Daily Commute on Duri?",
        answer:
          "Daily Commute is for people who travel the same route on weekdays — for example home to office and back. You set your start time, return time, and weekly days once. Duri then finds recurring Hosts or Guests on that corridor instead of you searching for a new ride every morning.",
        navigation: ["Home", "Daily Commute"],

        visual: { emoji: "💼", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "set-commute-profile",
        question: "How do I set up my commute profile?",
        answer:
          "In the app, open Profile → Commute Profile. Add your start location and departure time, return location and time, and which weekdays you travel (for example Monday–Friday or Mon–Wed–Fri only). Save once — Duri remembers your schedule for future matching.",
        navigation: ["Profile", "Commute Profile", "Add Schedule"],

        visual: { emoji: "🗓️", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "know-my-hosts-guests",
        question: "What are Know My Hosts / Know My Guests?",
        answer:
          "You can mark trusted commuters from your office, society, or past rides. Duri prioritises these people when matching your weekly commute, so you are more likely to ride with familiar, verified co-travellers.",
        navigation: ["Profile", "Commute Profile", "Know My Hosts / Guests"],

        visual: { emoji: "👥", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "auto-match-weekly",
        question: "How does auto-match and weekly booking work?",
        answer:
          "After your commute profile is saved, Duri's engine pairs Hosts with Guests on the same route and similar times, then can auto-book your seats for the week. You avoid repeating a manual search every day — your weekday rides stay on one plan.",
        navigation: ["Profile", "Commute Profile", "Weekly Auto-Book"],

        visual: { emoji: "🔁", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "partial-weekdays",
        question: "Can I commute only on certain weekdays?",
        answer:
          "Yes. Choose only the days you need — for example Monday, Wednesday, and Friday. Duri matches and books only for those days; you are not forced into a full Monday–Friday plan.",
        navigation: ["Profile", "Commute Profile", "Select Weekdays"],

        visual: { emoji: "📆", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "commute-delay-cancel",
        question: "What if someone is late or I need to skip one day?",
        answer:
          "Use delay alerts and in-ride chat to coordinate pickups and ETAs. If you cannot travel on a single day, cancel that day only — your weekly commute profile and remaining days stay active.",
        navigation: ["My Rides", "Upcoming Commute", "Cancel This Day"],

        visual: { emoji: "⏱️", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "commute-no-match",
        question: "What if no commute match is available for my slot?",
        answer:
          "Widen your time window slightly (for example ±15–30 minutes) or save your commute need. Duri notifies you when a matching Host or Guest appears. Hosts on busy office corridors can also see waiting commute demand and publish rides for those times.",
        navigation: ["Daily Commute", "No Match", "Save Commute Need"],

        visual: { emoji: "🔔", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "commute-monthly-savings",
        question: "How much can I save with daily commute carpool?",
        answer:
          "Compare the per-seat commute fare with what you spend solo — fuel, tolls, parking, or daily cab. Shared weekday rides often cut monthly travel cost sharply on routes like tech parks and city-to-city office corridors. Check fare details before you confirm each week.",
        navigation: ["Daily Commute", "View Fare", "Compare Monthly Cost"],

        visual: { emoji: "💸", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
      {
        id: "commute-on-website",
        question: "How do I explore daily commute carpool on the Duri website?",
        answer:
          "Tap Daily Commute Carpool in the header or open /daily-commute-carpool to read how it works, see popular office routes, and download the app to set up your commute profile. Full auto-match and weekly booking run in the mobile app after verification.",
        navigation: ["Header", "Daily Commute Carpool ", "Get the App"],

        visual: { emoji: "📱", gradient: "from-primary/20 via-secondary to-accent/15", caption: "Daily Commute" },
      },
    ],
  },
  {
    id: "comfort-preferences",
    title: "Comfort & Preferences",
    description:
      "Baggage rules, host & guest preferences, and in-ride facilities - set expectations upfront for hassle-free, joyful and memorable rides.",
    icon: "Sparkles",
    faqs: [
      {
        id: "baggage-rules",
        question: "Can the Host define baggage limits for a ride?",
        answer:
          "Yes. Hosts can predefine clear Baggage Rules per ride - for example 'cabin bag only', 'one medium suitcase per guest', 'no large luggage' or 'no baggage (commute ride)'. Guests see these rules before booking, so there are zero last-minute surprises at pickup. It keeps the boot space fair for everyone and supports Duri's mission of hassle-free, joyful rides.",
        navigation: ["Host a Ride", "Ride Rules", "Baggage Limit"],

        visual: { emoji: "🧳", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
      {
        id: "extra-baggage",
        question: "What if I'm carrying extra baggage as a Guest?",
        answer:
          "Always check the Host's baggage rule before booking. If you need extra space (large suitcase, sports gear, instruments), use the Group Chat or Special Request to ask the Host in advance - many Hosts happily accommodate when informed early. This upfront clarity keeps the ride safe, comfortable and memorable for everyone.",
        navigation: ["Search Rides", "Select Ride", "Special Request", "Extra Baggage"],

        visual: { emoji: "🎒", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
      {
        id: "host-guest-preferences",
        question: "Can a Host set preferences about who can join the ride?",
        answer:
          "Yes. Hosts can set Guest Preferences such as No Smoking, Working Professionals / IT employees, Business travelers, Sports enthusiasts, Music lovers, Students, Families, or Female-only co-riders. Guests see these tags before booking and can self-match, so the cabin vibe is right for everyone - making each ride hassle-free, safe and genuinely joyful.",
        navigation: ["Host a Ride", "Preferences", "Guest Preferences"],

        visual: { emoji: "🏷️", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
      {
        id: "no-smoking-rule",
        question: "How do No-Smoking and similar rules work?",
        answer:
          "When a Host enables rules like No Smoking, No Alcohol, No Pets, or No Loud Calls, Guests must accept them at booking. Breaking a rule mid-ride can be reported via Grievance and impacts the rider's trust score. Clear rules upfront mean no awkward moments later - pure joyful, memorable travel.",
        navigation: ["Host a Ride", "Ride Rules"],

        visual: { emoji: "🚭", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
      {
        id: "in-ride-facilities",
        question: "Can the Host advertise in-ride facilities like Music, Wi-Fi or Leather seats?",
        answer:
          "Absolutely. Hosts can showcase In-Ride Facilities such as Music / Aux / Bluetooth, Onboard Wi-Fi hotspot, Leather seats, Reclining seats, AC strength, Phone chargers (USB-C / fast charge), Water bottles, Reading lights, and even a curated road-trip playlist. Guests filter and pick rides that match their comfort - turning a routine commute into a joyful, memorable journey.",
        navigation: ["Host a Ride", "Vehicle Facilities", "Add Amenities"],

        visual: { emoji: "📶", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
      {
        id: "ride-bio-hobbies",
        question: "What is the Ride Bio and Host hobbies section?",
        answer:
          "Hosts can add a short Ride Bio describing themselves, their profession, hobbies, favorite music genres and conversation style (chatty, quiet, podcast-friendly). This helps Guests find like-minded companions through Companion Match - perfect for long journeys where good company makes the ride safe, joyful and truly memorable.",
        navigation: ["Profile", "Host Profile", "Ride Bio & Hobbies"],

        visual: { emoji: "🎸", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
      {
        id: "guest-preferences-set",
        question: "Can Guests also set their own travel preferences?",
        answer:
          "Yes. Guests can save preferences like preferred seat (window/aisle/front), gender of co-riders, music tolerance, AC needs, conversation style, and accessibility requirements. Duri's AI matcher uses these to suggest the most comfortable rides - so every booking starts hassle-free and ends as a joyful, memorable trip.",
        navigation: ["Profile", "Travel Preferences", "Edit"],

        visual: { emoji: "⚙️", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
      {
        id: "avoid-surprises",
        question: "How do these preferences avoid last-minute surprises?",
        answer:
          "Baggage limits, no-smoking rules, in-ride facilities, seat layout and Host bio are all visible before you confirm a booking - and re-shown on your boarding ticket. No mismatched expectations, no awkward conversations at pickup. This transparency is core to Duri's mission: hassle-free, safe, joyful and memorable rides for every Host and Guest.",
        navigation: ["Search Rides", "Select Ride", "Ride Details & Rules"],

        visual: { emoji: "🎯", gradient: "from-accent/20 via-primary/10 to-secondary", caption: "Comfort & Vibe" },
      },
    ],
  },
  {
    id: "fare-payments",
    title: "Fares & Payments",
    description: "How fares are calculated, receipts, and rewards.",
    icon: "Wallet",
    faqs: [
      {
        id: "fare-engine",
        question: "How is the fare calculated?",
        answer:
          "Duri uses an AI-driven Fare Engine that suggests per-seat fares based on vehicle make/model, fuel type, distance, tolls, and seat layout. Hosts see a recommended fare and can fine-tune it. Fares are shown in INR (India) and USD (USA Beta), with multi-currency support for future markets.",
        navigation: ["Host a Ride", "Fare", "AI Suggested Fare"],

        visual: { emoji: "🧮", gradient: "from-amber-400/20 via-primary/10 to-secondary", caption: "Fair Fares" },
      },
      {
        id: "ride-receipt",
        question: "Can I download a receipt for expense claims?",
        answer:
          "Yes. Open Ride History, select the trip, and download a Ride Receipt - useful for office reimbursements and travel expense claims.",
        navigation: ["Profile", "Ride History", "Select Ride", "Download Receipt"],

        visual: { emoji: "🧾", gradient: "from-amber-400/20 via-primary/10 to-secondary", caption: "Fair Fares" },
      },
      {
        id: "host-reward",
        question: "What is the Host Onboard Reward?",
        answer:
          "Verified Hosts receive ₹125 (India) / $2 (USA Beta) after completing their first ride - credited automatically to your Duri wallet.",
        navigation: ["Profile", "Wallet", "Rewards"],

        visual: { emoji: "🎁", gradient: "from-amber-400/20 via-primary/10 to-secondary", caption: "Fair Fares" },
      },
      {
        id: "referral-reward",
        question: "How does the Referral Reward work?",
        answer:
          "Refer a friend to host on Duri. When your referred Host completes their first ride, you earn ₹100 (India) / $1.50 (USA Beta) instantly.",
        navigation: ["Profile", "Refer & Earn", "Share Code"],

        visual: { emoji: "💌", gradient: "from-amber-400/20 via-primary/10 to-secondary", caption: "Fair Fares" },
      },
    ],
  },
  {
    id: "pricing",
    title: "Pricing & Cost",
    description: "Is Duri free? What does it cost to use the app?",
    icon: "BadgeIndianRupee",
    faqs: [
      {
        id: "is-duri-free",
        question: "Will there be any cost to use the Duri app?",
        answer:
          "The Duri app is 100% FREE to download and use - for both Hosts and Guests. There are no subscription fees, no booking fees, and no platform commissions today. The only money that changes hands is the per-seat fare a Guest pays the Host to share fuel and toll cost - and that goes entirely to the Host, not to Duri.",
        navigation: ["Home", "Pricing"],

        visual: { emoji: "🆓", gradient: "from-emerald-500/20 via-primary/10 to-secondary", caption: "100% Free" },
      },
      {
        id: "hidden-charges",
        question: "Are there any hidden charges or commissions?",
        answer:
          "No hidden charges. No surge pricing. No platform commission deducted from the Host's fare. We even reward verified Hosts (₹125 / $2 first-ride bonus) and pay referral rewards (₹100 / $1.50). Duri is currently focused on building the community first.",
        navigation: ["Profile", "Wallet", "Transactions"],

        visual: { emoji: "🚫", gradient: "from-emerald-500/20 via-primary/10 to-secondary", caption: "100% Free" },
      },
      {
        id: "cheaper-than-taxi",
        question: "How much can I save compared to a taxi or bus?",
        answer:
          "Typical Duri intercity fares are 50-70% lower than Uber/Ola intercity quotes and often comparable to or cheaper than AC bus tickets - while you travel in a private car with a verified Host. Exact savings depend on route, vehicle and number of co-riders.",
        navigation: ["Search Rides", "Fare Comparison"],

        visual: { emoji: "📉", gradient: "from-emerald-500/20 via-primary/10 to-secondary", caption: "100% Free" },
      },
      {
        id: "estimate-savings-before-ride",
        question: "How do I know approximately how much I can save before the ride?",
        answer:
          "Before you confirm a booking, Duri shows the per-seat fare for your chosen ride alongside the AI-suggested fair share for that route, vehicle, distance, and tolls. Use Fare Comparison in ride search to see how the Duri seat price stacks up against typical taxi, bus, or solo-drive cost for the same corridor - so you know your approximate savings upfront, not after the trip.",
        navigation: ["Search Rides", "Select Ride", "Fare Comparison"],

        visual: { emoji: "💰", gradient: "from-emerald-500/20 via-primary/10 to-secondary", caption: "100% Free" },
      },
    ],
  },
  {
    id: "during-ride",
    title: "During the Ride",
    description: "Communication, group chat, air quality, and live updates.",
    icon: "MessageCircle",
    faqs: [
      {
        id: "ride-group-chat",
        question: "Can I chat with co-riders before/during the trip?",
        answer:
          "Yes. Each ride has a Ride Group Chat where the Host and all booked Guests can coordinate pickup, share live updates, and stay in sync.",
        navigation: ["My Rides", "Select Ride", "Group Chat"],

        visual: { emoji: "💬", gradient: "from-primary/15 via-secondary to-accent/15", caption: "On the Road" },
      },
      {
        id: "call-rider",
        question: "Can I call the Host or Guest directly?",
        answer:
          "Yes - use the Call Rider option inside the ride. Calls are masked for privacy when needed.",
        navigation: ["My Rides", "Select Ride", "Call Rider"],

        visual: { emoji: "📞", gradient: "from-primary/15 via-secondary to-accent/15", caption: "On the Road" },
      },
      {
        id: "delays",
        question: "How do I communicate a delay?",
        answer:
          "Use Communicate Delays in advance from the ride screen. All co-riders are notified instantly with the new ETA.",
        navigation: ["Active Ride", "Notify Delay", "Update ETA"],

        visual: { emoji: "⏱️", gradient: "from-primary/15 via-secondary to-accent/15", caption: "On the Road" },
      },
      {
        id: "air-quality",
        question: "Does the app suggest health advice based on Air Quality (AQI) of my route?",
        answer:
          "Yes. Duri pulls live AQI data for your current location and full route, and shows clear health advisories - for example, when AQI is poor it suggests keeping windows closed, switching AC to recirculation, wearing a mask at stops, and is especially helpful for asthma, elderly and child travelers. Hosts also see this so they can plan a healthier route or rest stop.",
        navigation: ["Active Ride", "Trip Info", "Air Quality & Health Advice"],

        visual: { emoji: "🌬️", gradient: "from-primary/15 via-secondary to-accent/15", caption: "On the Road" },
      },
      {
        id: "feedback",
        question: "How do I leave ride feedback?",
        answer:
          "After every ride, both Hosts and Guests are prompted to rate each other and leave Ride Feedback. Ratings build trust scores visible to future co-riders.",
        navigation: ["Ride History", "Completed Ride", "Leave Feedback"],

        visual: { emoji: "⭐", gradient: "from-primary/15 via-secondary to-accent/15", caption: "On the Road" },
      },
    ],
  },
  {
    id: "privacy-policies",
    title: "Privacy & Policies",
    description: "How Duri handles your data, identity, and consent.",
    icon: "Lock",
    faqs: [
      {
        id: "data-collection",
        question: "What personal data does Duri collect?",
        answer:
          "Duri collects your phone number, ID verification status (not the raw number), driving licence and RC for Hosts, location during active rides, and ride history. We never sell your data to third parties.",
        navigation: ["Profile", "Privacy Center", "Data Collected"],

        visual: { emoji: "📊", gradient: "from-slate-500/15 via-secondary to-primary/10", caption: "Privacy First" },
      },
      {
        id: "location-privacy",
        question: "Is my live location shared with strangers?",
        answer:
          "No. Live location is only shared with your booked co-riders for that specific ride and with emergency contacts you nominate. It stops sharing the moment the ride ends.",
        navigation: ["Profile", "Privacy Center", "Location Sharing"],

        visual: { emoji: "📍", gradient: "from-slate-500/15 via-secondary to-primary/10", caption: "Privacy First" },
      },
      {
        id: "delete-account",
        question: "Can I delete my account and data?",
        answer:
          "Yes. Open Settings → Account → Delete Account. Your personal data is permanently erased within 30 days, except information we're legally required to retain.",
        navigation: ["Profile", "Settings", "Account", "Delete Account"],

        visual: { emoji: "🗑️", gradient: "from-slate-500/15 via-secondary to-primary/10", caption: "Privacy First" },
      },
      {
        id: "phone-masking",
        question: "Is my phone number visible to other users?",
        answer:
          "No. In-app calls are routed through masked numbers. Your real number is never displayed to other riders.",
        navigation: ["Profile", "Privacy Center", "Call Privacy"],

        visual: { emoji: "☎️", gradient: "from-slate-500/15 via-secondary to-primary/10", caption: "Privacy First" },
      },
    ],
  },
  {
    id: "legal-compliance",
    title: "Legal & Compliance",
    description: "Terms, grievances, and regulatory compliance.",
    icon: "Scale",
    faqs: [
      {
        id: "is-duri-legal",
        question: "Is intercity ride sharing on Duri legal?",
        answer:
          "Yes. Duri facilitates expense-sharing between private car owners and co-riders on shared routes - a legitimate carpooling model recognized in India and supported in the USA Beta states. Hosts are not commercial taxi operators; fares only cover trip expenses.",
        navigation: ["Profile", "Legal", "Carpooling Policy"],

        visual: { emoji: "⚖️", gradient: "from-secondary via-primary/10 to-accent/10", caption: "Compliant" },
      },
      {
        id: "raise-complaint",
        question: "How do I raise a complaint or grievance?",
        answer:
          "Open Ride Compliance and Grievance from the menu, file your complaint against a Rider or about the App, attach evidence if any, and track its status until resolution.",
        navigation: ["Profile", "Help & Support", "Raise Grievance"],

        visual: { emoji: "📝", gradient: "from-secondary via-primary/10 to-accent/10", caption: "Compliant" },
      },
      {
        id: "insurance",
        question: "Are rides covered by insurance?",
        answer:
          "Hosts must maintain valid vehicle insurance as required by local law. Duri verifies insurance validity during onboarding. Personal travel insurance for Guests is recommended for long trips.",
        navigation: ["Host Profile", "My Vehicles", "Insurance"],

        visual: { emoji: "🛡️", gradient: "from-secondary via-primary/10 to-accent/10", caption: "Compliant" },
      },
      {
        id: "terms",
        question: "Where do I read the full Terms and Privacy Policy?",
        answer:
          "Both documents are available inside the app under Settings → Legal, and on duriapp.com. We notify users on every material update.",
        navigation: ["Profile", "Settings", "Legal", "Terms & Privacy"],

        visual: { emoji: "📜", gradient: "from-secondary via-primary/10 to-accent/10", caption: "Compliant" },
      },
    ],
  },
  {
    id: "support",
    title: "Support & Help",
    description: "Reach Duri for documents, disputes, or any help you need.",
    icon: "Headphones",
    faqs: [
      {
        id: "contact-support",
        question: "How do I contact Duri support?",
        answer:
          "Reach us via in-app chat, WhatsApp, or our call center phone line. Telugu and English support available in India; English support in USA Beta.",
        navigation: ["Profile", "Help & Support", "Contact Us"],

        visual: { emoji: "📞", gradient: "from-primary/20 via-secondary to-accent/15", caption: "24/7 Help" },
      },
      {
        id: "manual-verification",
        question: "What if my documents fail auto-verification?",
        answer:
          "Our call center team performs Manual Document Validation. Submit clear photos and our agents verify within 24-48 hours.",
        navigation: ["Profile", "Verification Center", "Request Manual Review"],

        visual: { emoji: "👁️", gradient: "from-primary/20 via-secondary to-accent/15", caption: "24/7 Help" },
      },
      {
        id: "notifications",
        question: "What notifications will I receive?",
        answer:
          "Push notifications for ride bookings, cancellations, matching availability for your saved searches, group chat messages, SOS confirmations, and reward credits.",
        navigation: ["Profile", "Settings", "Notifications"],

        visual: { emoji: "🔔", gradient: "from-primary/20 via-secondary to-accent/15", caption: "24/7 Help" },
      },
    ],
  },
];
